import moment from "../../../../../moment";
import { DATE_SHORT_FORMAT } from "../../../../../components/DatePicker";
import { FORM_ITEM_DATE_PICKER } from "../../../../../components/Form/FormItems/FormItemDatePicker";
import { FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING } from "../../../../../components/Form/FormItems/FormItemResourceSelectLazyLoading";
import { FORM_ITEM_TEXT } from "../../../../../components/Form/FormItems/FormItemText";
import config from "../../../../../config";

/**
 * Returns the list of safety estimation options translated to the active locale.
 * Used by the "estimation" field to classify the overall safety impact of the incident.
 *
 * @param {Function} t - react-i18next translation function
 * @returns {{ value: string, text: string }[]} Array of estimation select options
 */
const estimationOptions = (t) => [
  { value: "positive", text: t("ior.new_report.estimation.positive") },
  { value: "negative", text: t("ior.new_report.estimation.negative") },
  { value: "neutral", text: t("ior.new_report.estimation.neutral") }
];

/**
 * Returns the list of responsible-party type options (internal employee vs. external entity).
 * Exported so other parts of the IOR module can share the same translated options.
 *
 * @param {Function} t - react-i18next translation function
 * @returns {{ value: string, text: string }[]} Array of responsible-type select options
 */
export const typeOptions = (t) => [
  { value: "internal", text: t("ior.new_report.responsible.internal") },
  { value: "external", text: t("ior.new_report.responsible.external") }
];

/**
 * Builds the full list of form field definitions for the Investigation section.
 * All fields with `required: hasData` become mandatory only when at least one
 * investigation field has been filled in by the user.
 *
 * @param {object}   params
 * @param {boolean}  params.hasData        - Whether any investigation field has a value.
 * @param {object}   params.data           - Full report data object including basic_data and investigation.
 * @param {Function} params.t              - react-i18next translation function.
 * @param {object}   params.form           - Ant Design form instance.
 * @param {string}   params.type           - Report mode: "new" | "edit" | "view".
 * @param {object}   params.initialValues  - Pre-populated investigation values for an existing report.
 * @returns {Array} Array of field definition objects for renderDynamicallyFields.
 */
export const buildInvestigationFields = ({ hasData, data, t, form, type, initialValues }) => [
  {
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    required: hasData,
    fieldProps: {
      requestOnMount: true
    },
    apiRoute:
      "ior/types?field%5B%5D=active&op%5B%5D=eq&order_by_column%5B%5D=name&order_by_type%5B%5D=asc&term%5B%5D=1" +
      (data.investigation?.type_id ? "&selected_item_id=" + data.investigation.type_id : ""),
    name: ["investigation", "type_id"],
    label: "ior.new_report.type",
    placeholder: "ior.new_report.type"
  },
  {
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    fieldProps: {
      requestOnMount: true,
      mode: "multiple",
      transformer: (options) =>
        options.map((option) => ({ ...option, className: !option.active && "option-select-hidden" }))
    },
    apiRoute: "ior/tags?order_by_column%5B%5D=name&order_by_type%5B%5D=asc&term%5B%5D=1",
    name: ["investigation", "tags"],
    label: "ior.new_report.tags",
    placeholder: "ior.new_report.tags"
  },
  {
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    required: hasData,
    fieldProps: {
      requestOnMount: true
    },
    apiRoute:
      "ior/carriers?field%5B%5D=active&op%5B%5D=eq&order_by_column%5B%5D=name&order_by_type%5B%5D=asc&term%5B%5D=1" +
      (data.investigation?.carrier_id ? "&selected_item_id=" + data.investigation.carrier_id : ""),
    name: ["investigation", "carrier_id"],
    label: "ior.new_report.carrier",
    placeholder: "ior.new_report.carrier"
  },
  {
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    required: hasData,
    fieldProps: {
      requestOnMount: true
    },
    apiRoute:
      "ior/categories?field%5B%5D=active&op%5B%5D=eq&order_by_column%5B%5D=name&order_by_type%5B%5D=asc&term%5B%5D=1" +
      (data.investigation?.category_id ? "&selected_item_id=" + data.investigation.category_id : ""),
    name: ["investigation", "category_id"],
    label: "ior.new_report.category",
    placeholder: "ior.new_report.category"
  },
  {
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    required: hasData,
    fieldProps: {
      requestOnMount: true
    },
    apiRoute:
      "ior/issues?field%5B%5D=active&op%5B%5D=eq&order_by_column%5B%5D=name&order_by_type%5B%5D=asc&term%5B%5D=1" +
      (data.investigation?.issue_id ? "&selected_item_id=" + data.investigation.issue_id : ""),
    name: ["investigation", "issue_id"],
    label: "ior.new_report.issue",
    placeholder: "ior.new_report.issue"
  },
  {
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    required: hasData,
    fieldProps: {
      requestOnMount: true
    },
    apiRoute:
      "ior/liabilities?field%5B%5D=active&op%5B%5D=eq&order_by_column%5B%5D=name&order_by_type%5B%5D=asc&term%5B%5D=1" +
      (data.investigation?.liability_id ? "&selected_item_id=" + data.investigation.liability_id : ""),
    name: ["investigation", "liability_id"],
    label: "ior.new_report.liability",
    placeholder: "ior.new_report.liability"
  },
  {
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    required: hasData,
    fieldProps: {
      requestOnMount: true
    },
    // The legacy single root-cause field is only shown for reports that were created before
    // the multi-select root-cause system was introduced; specifically when the report is
    // archived/closed and the root_causes_old_format flag is set on the basic data
    visible:
      type !== "new" &&
      (data.basic_data.stage === "archived" || data.basic_data.stage === "closed") &&
      data.basic_data?.root_causes_old_format === 1,
    apiRoute:
      "ior/root-causes?field%5B%5D=active&op%5B%5D=eq&order_by_column%5B%5D=name&order_by_type%5B%5D=asc&term%5B%5D=1",
    name: ["investigation", "root_cause_id"],
    label: "ior.new_report.root_cause",
    placeholder: "ior.new_report.root_cause"
  },
  {
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    required: hasData,
    fieldProps: {
      staticOptions: estimationOptions(t)
    },
    name: ["investigation", "estimation"],
    label: "ior.new_report.estimation.label",
    placeholder: "ior.new_report.estimation.label"
  },
  {
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    required: hasData,
    fieldProps: {
      staticOptions: typeOptions(t),
      onChange: () => {
        const investigation = form.getFieldValue("investigation");
        form.setFieldsValue({
          investigation: {
            ...investigation,
            responsible_name: undefined,
            responsible_id: undefined
          }
        });
      }
    },
    name: ["investigation", "responsible_type"],
    label: "ior.new_report.responsible.caused_by_type",
    placeholder: "ior.new_report.responsible.caused_by_type"
  },
  {
    type: FORM_ITEM_TEXT,
    max: 200,
    visible: (formInstance) => formInstance.getFieldValue(["investigation", "responsible_type"]) === "external",
    name: ["investigation", "responsible_name"],
    label: "ior.new_report.responsible.caused_by_name",
    placeholder: "ior.new_report.responsible.caused_by_name"
  },
  {
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    fieldProps: {
      searchColumns: ["name"]
    },
    initialValue: initialValues?.responsible_id
      ? {
          id: initialValues.responsible_id,
          text: initialValues.responsible_internal_name
        }
      : undefined,
    required: true,
    visible: (formInstance) => formInstance.getFieldValue(["investigation", "responsible_type"]) === "internal",
    name: ["investigation", "responsible_id"],
    apiRoute:
      "users/all?field%5B%5D=user_is_in_organisation&filters%5BvalidContract%5D=1&op%5B%5D=eq&order_by_column%5B%5D=name&order_by_type%5B%5D=asc&term%5B%5D=1",
    label: "ior.new_report.responsible.caused_by_name",
    placeholder: "ior.new_report.responsible.caused_by_name"
  },
  {
    type: FORM_ITEM_DATE_PICKER,
    required: hasData,
    name: ["investigation", "deadline"],
    label: "ior.new_report.deadline",
    placeholder: "ior.new_report.deadline",
    disabledDate: (current) => {
      if (!form.getFieldValue(["basic_data", "event_date"])) {
        return false;
      }
      const fullDate = `${moment(form.getFieldValue(["basic_data", "event_date"])).format(
        config.defaultDateFormat
      )} ${moment(form.getFieldValue(["basic_data", "event_time"])).format("HH:mm")}`;

      return moment(current, DATE_SHORT_FORMAT).startOf("day") < moment(fullDate);
    }
  }
];
